import React, { useCallback, useState, useEffect } from 'react';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import debounce from 'lodash/debounce';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type OptionType = {
  fullName: string;
  email: string;
  id: string;
};

type Props = {
  options: Array<OptionType>;
  value: string;
  onChange: Function;
  validate?: Function;
  isLoading?: boolean;
};

type OptionProps = {
  onChange: Function;
  option: OptionType;
};

function Option({ onChange, option: { fullName, email } }: OptionProps) {
  const handleClick = useCallback(() => {
    onChange(email);
  }, [onChange, email]);

  return (
    <div className="simple-add-teammate-modal-select-option" onClick={handleClick}>
      <p className="font-weight-bold m-0 pl-3">{fullName}</p>
      <p className="text-muted m-0 pl-3">{email}</p>
    </div>
  );
}

export default function Select({ options, value, onChange, validate, isLoading }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const { tr } = useTranslation();

  const debounceValidate = useCallback(
    debounce(fieldValue => setError(validate(fieldValue)), 100),
    [validate]
  );

  const handleInputChange = useCallback(
    ({ target: { value: fieldValue } }) => {
      onChange(fieldValue);
      debounceValidate(fieldValue);
    },
    [debounceValidate, onChange]
  );

  const handleDropdownChange = useCallback(
    fieldValue => {
      onChange(fieldValue);
      setIsOpen(false);
      debounceValidate(fieldValue);
    },
    [debounceValidate, onChange]
  );

  const handleDropdownOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    setTouched(true);
    setTimeout(() => setIsOpen(false), 200);
  }, []);

  const filteredOptions = options.filter(option => option.email !== value && option.email.includes(value));

  useEffect(() => {
    if (filteredOptions.length && touched) setIsOpen(true);
  }, [filteredOptions.length, touched]);

  return (
    <div>
      <Dropdown isOpen={isOpen} toggle={handleDropdownOpen}>
        <DropdownToggle tag="span" data-toggle="dropdown" aria-expanded={isOpen}>
          <input
            className="form-control bg-white"
            type="email"
            name="email"
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={tr('perform.workspace.simple_add_teammate.enter_email_or_leave_blank')}
            autoComplete="off"
          />
        </DropdownToggle>
        {filteredOptions.length && !isLoading ? (
          <DropdownMenu className="w-100">
            {isLoading ? (
              <div className="spinner-border" role="status" />
            ) : (
              filteredOptions.map(option => <Option key={option.id} option={option} onChange={handleDropdownChange} />)
            )}
          </DropdownMenu>
        ) : null}
      </Dropdown>

      <div className="simple-add-teammate-modal-select-error w-100">
        {touched && value && error ? <p className="text-danger position-absolute small pb-1  m-0">{error}</p> : null}
      </div>
    </div>
  );
}
